import { type MessageDescriptor, defineMessages, useIntl } from "react-intl";

import type { NavigationCategory } from "~/components/hec-header/mobile-nav/types";
import { HorizontalSlider } from "~/components/horizontal-slider";
import { builderImageUrl } from "~/hec/utils/builder-image-url";
import { cn } from "~/lib/ui";

import {
  type Chip,
  LABELLED_CHIP_IDS,
  type LabelledChipId,
  chipFamily,
  defaultChipId,
} from "./chips";

const messages = defineMessages({
  label: { id: "spenderChipRow.label", defaultMessage: "Shop by category" },
  root: { id: "spenderChipRow.root", defaultMessage: "All" },
  bestSellers: {
    id: "spenderChipRow.best_sellers",
    defaultMessage: "Best Sellers",
  },
  bundles: { id: "spenderChipRow.bundles", defaultMessage: "Bundles" },
});

const FALLBACK_LABELS: Record<LabelledChipId, MessageDescriptor> = {
  root: messages.root,
  "best-sellers": messages.bestSellers,
  "all-bundles": messages.bundles,
};
const LABELLED_IDS: readonly string[] = LABELLED_CHIP_IDS;
const isLabelled = (id: string): id is LabelledChipId =>
  LABELLED_IDS.includes(id);

// Radix spacing keys 1..9 = 4,8,12,16,24,32,40,48,64px: h-6 = 32, md:h-7 = 40.
const CHIP_CLASS =
  "inline-flex h-6 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border-[0.5px] border-foreground bg-gray-01 px-3 font-grotesque text-sm text-emphasis no-underline hover:bg-gray-02 focus-visible:-outline-offset-4 md:h-7 md:gap-2 md:px-4 md:text-base";
// Inverted so it outranks hover; the inset accent ring vanishes on this fill.
const PRESSED_CLASS =
  "border-emphasis bg-emphasis text-white hover:bg-emphasis focus-visible:outline-white";

/**
 * COM-261 chip row: the authored chips (default: the live Fastr page's 7, in
 * its order). Controlled: filters the page's grid in place, no links. A chip's
 * icon is a Builder image (its authored upload, else its category's
 * `category-enrichment` icon, the PLP pills' source); without one it is text
 * only, as a PLP category pill is.
 */
export function SpenderChipRow({
  chips,
  categories,
  selectedId,
  onSelect,
  className,
}: {
  chips: readonly Chip[];
  categories: readonly NavigationCategory[];
  selectedId: string;
  onSelect: (id: string) => void;
  className?: string;
}) {
  const intl = useIntl();

  const byId = new Map(categories.map(c => [c.id, c]));
  const family = chipFamily(selectedId, categories, chips);

  return (
    <nav
      aria-label={intl.formatMessage(messages.label)}
      data-testid="spender-chip-row"
      className={cn("relative mt-5", className)}
    >
      <HorizontalSlider controlsEnabled>
        {chips.map(({ id, label, iconUrl }) => {
          const pressed = family === id;
          return (
            <button
              key={id}
              type="button"
              aria-pressed={pressed}
              onClick={() => onSelect(pressed ? defaultChipId(chips) : id)}
              data-testid={`spender-chip-button-${id}`}
              className={cn(CHIP_CLASS, pressed && PRESSED_CLASS)}
            >
              {iconUrl && (
                <img
                  // 2x the 20px desktop slot; a no-op for non-Builder URLs.
                  src={builderImageUrl(iconUrl, { width: 40, format: "webp" })}
                  // Decorative: the button's accessible name is its label alone.
                  alt=""
                  aria-hidden="true"
                  decoding="async"
                  className="h-4 w-4 shrink-0 rounded-full object-cover md:h-5 md:w-5"
                />
              )}
              {label ??
                (isLabelled(id)
                  ? intl.formatMessage(FALLBACK_LABELS[id])
                  : (byId.get(id)?.name ?? id))}
            </button>
          );
        })}
      </HorizontalSlider>
    </nav>
  );
}
